import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { seasonsService } from '../../services/db';
import { ITEMS_PER_PAGE, SEASON_STATUS_CONFIG } from '../../constants';
import { ROUTES } from '../../constants/routes';
import Table from '../../components/ui/Table';
import Pagination from '../../components/ui/Pagination';
import Spinner from '../../components/ui/Spinner';
import {
  TrophyIcon,
  LightningIcon,
  BibleIcon,
  CrownIcon,
  ShieldIcon,
  SearchIcon,
  TargetIcon,
  BooksIcon,
  PencilEditIcon,
  EyeIcon,
  TrashIcon,
  LinkIcon,
  BarChartIcon,
  ClockIcon,
} from '../../components/common/Icons';

const statusThemeMap = {
  'active': { bg: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: 'rgba(16, 185, 129, 0.25)' },
  'approved': { bg: 'rgba(59, 130, 246, 0.1)', color: '#2563EB', border: 'rgba(59, 130, 246, 0.25)' },
  'under-review': { bg: 'rgba(245, 158, 11, 0.1)', color: '#D97706', border: 'rgba(245, 158, 11, 0.25)' },
  'completed': { bg: 'rgba(107, 114, 128, 0.1)', color: '#4B5563', border: 'rgba(107, 114, 128, 0.25)' },
  'rejected': { bg: 'rgba(239, 68, 68, 0.1)', color: '#DC2626', border: 'rgba(239, 68, 68, 0.25)' },
  'suspended': { bg: 'rgba(239, 68, 68, 0.1)', color: '#DC2626', border: 'rgba(239, 68, 68, 0.25)' },
  'deactivated': { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280', border: 'rgba(107, 114, 128, 0.2)' }
};

const roleMap = {
  'Super Admin': { bg: 'rgba(239, 68, 68, 0.08)', text: '#DC2626', border: 'rgba(239, 68, 68, 0.24)', icon: <LightningIcon size={13} /> },
  'Admin': { bg: 'rgba(249, 115, 22, 0.08)', text: '#EA580C', border: 'rgba(249, 115, 22, 0.24)', icon: <ShieldIcon size={13} /> },
  'Owner': { bg: 'rgba(16, 185, 129, 0.08)', text: '#059669', border: 'rgba(16, 185, 129, 0.24)', icon: <CrownIcon size={13} /> },
  'Contributor': { bg: 'rgba(37, 99, 235, 0.08)', text: '#2563EB', border: 'rgba(37, 99, 235, 0.24)', icon: <PencilEditIcon size={13} /> },
  'Reader': { bg: 'rgba(107, 114, 128, 0.08)', text: '#4B5563', border: 'rgba(107, 114, 128, 0.2)', icon: <EyeIcon size={13} /> }
};


const ManageMyQuizzes = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const {
    isSeasonOwner,
    getSeasonRole
  } = usePermissions();

  const [seasons, setSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [seasonsSearch, setSeasonsSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [seasonsPage, setSeasonsPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Load user seasons (normal host view only)
  const loadSeasons = async () => {
    if (!user || !user.email) return;
    setLoadingSeasons(true);
    try {
      const data = await seasonsService.getByHostEmail(user.email);
      setSeasons(data || []);
    } catch (err) {
      console.error('Failed to load seasons:', err);
      showToast('Failed to load seasons.', 'error');
    } finally {
      setLoadingSeasons(false);
    }
  };

  useEffect(() => {
    if (user && user.email) {
      loadSeasons();
    } else {
      setLoadingSeasons(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Close actions dropdown on click outside
  useEffect(() => {
    const handleGlobalClick = () => setOpenMenuId(null);
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  // Compute live KPI metrics
  const activeLeagues = useMemo(() => seasons.filter(s => s.status === 'active' && s.status !== 'deactivated'), [seasons]);
  const approvedLeagues = useMemo(() => seasons.filter(s => s.status === 'approved' && s.status !== 'deactivated'), [seasons]);
  const reviewLeagues = useMemo(() => seasons.filter(s => s.status === 'under-review' && s.status !== 'deactivated'), [seasons]);
  const completedLeagues = useMemo(() => seasons.filter(s => s.status === 'completed' && s.status !== 'deactivated'), [seasons]);
  const nonDeactivatedSeasons = useMemo(() => seasons.filter(s => s.status !== 'deactivated'), [seasons]);
  
  const totalQuizzes = useMemo(() => 
    nonDeactivatedSeasons.reduce((sum, s) => sum + (Number(s.quizzes_count) || 0), 0),
    [nonDeactivatedSeasons]
  );

  // Filter seasons based on tab and search
  const filteredSeasons = useMemo(() => {
    return nonDeactivatedSeasons.filter((s) => {
      const matchesSearch = !seasonsSearch ||
        s.season?.toLowerCase().includes(seasonsSearch.toLowerCase()) ||
        s.description?.toLowerCase().includes(seasonsSearch.toLowerCase());
      if (!matchesSearch) return false;

      if (statusFilter === 'all') return true;
      if (statusFilter === 'active') return s.status === 'active';
      if (statusFilter === 'approved') return s.status === 'approved';
      if (statusFilter === 'under-review') return s.status === 'under-review';
      if (statusFilter === 'completed') return s.status === 'completed';
      return true;
    });
  }, [nonDeactivatedSeasons, seasonsSearch, statusFilter]);

  const pageSize = viewMode === 'grid' ? 9 : ITEMS_PER_PAGE;
  const totalSeasonsPages = Math.max(1, Math.ceil(filteredSeasons.length / pageSize));
  const pageSeasonsData = filteredSeasons.slice((seasonsPage - 1) * pageSize, seasonsPage * pageSize);

  // Actions
  const handleOpenSeasonDashboard = (row) => {
    const isUnderReview = row.status === 'under-review';
    const isRejected = row.status === 'rejected';
    const isSuspended = row.status === 'suspended';
    const canAccessDashboard = !isUnderReview && !isRejected && !isSuspended;

    if (!canAccessDashboard) {
      if (isRejected) {
        showToast('This season proposal was rejected by the Bible Contest admin team and cannot be accessed. You may deactivate it.', 'error');
      } else if (isSuspended) {
        showToast('This season has been suspended and is currently unavailable.', 'warning');
      } else {
        showToast('The season is under review, you will get notified once the season is ready to host.', 'info');
      }
      return;
    }
    const seasonObj = { id: row.id, season_id: row.id, ...row };
    localStorage.setItem('selected_season', JSON.stringify(seasonObj));
    navigate(`${ROUTES.STUDIO.SEASON_DASHBOARD}?seasonId=${row.id}`, {
      state: { seasonId: row.id, seasonData: seasonObj }
    });
  };

  const handleShareSeason = async (row) => {
    const shareUrl = `${window.location.origin}/play/season/${row.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: row.season,
          text: row.description,
          url: shareUrl
        });
      } catch (err) {
        console.log('Share dismissed', err);
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      showToast('Link copied to clipboard!', 'success');
    }
  };

  const handleDeactivateSeason = async (row) => {
    const ok = await confirm({
      title: 'Deactivate Season?',
      message: `Are you sure you want to deactivate "${row.season}"? This will archive the season.`,
      confirmText: 'Deactivate',
      cancelText: 'Keep Active',
      variant: 'danger',
      icon: '🗑️',
    });
    if (ok) {
      await seasonsService.delete(row.id);
      showToast(`Deactivated ${row.season}`, 'info');
      loadSeasons();
    }
  };

  // Table columns for desktop list view
  const seasonsColumns = [
    {
      key: 'season',
      label: 'Season Name',
      render: (_, row) => {
        const isUnderReview = row.status === 'under-review';
        const isRejected = row.status === 'rejected';
        const isSuspended = row.status === 'suspended';
        const canAccessDashboard = !isUnderReview && !isRejected && !isSuspended;
        return (
          <div>
            <span
              className={canAccessDashboard ? "link-cell" : ""}
              onClick={() => handleOpenSeasonDashboard(row)}
              style={{
                fontWeight: 700,
                color: canAccessDashboard ? 'var(--clr-primary)' : 'var(--clr-text-1)',
                cursor: canAccessDashboard ? 'pointer' : 'default',
                textDecoration: canAccessDashboard ? undefined : 'none'
              }}
              title={isUnderReview ? 'The season is under review, you will get notified once the season is ready to host.' : undefined}
            >
              {row.season}
            </span>
            {isUnderReview && (
              <div
                style={{
                  fontSize: '11.5px',
                  color: '#D97706',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontWeight: 500
                }}
              >
                <span>⏳</span>
                <span>The season is under review, you will get notified once the season is ready to host.</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'type',
      label: 'Format',
      render: (val) => {
        const isSingle = val === 'Single Contest';
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '11.5px',
              fontWeight: 600,
              letterSpacing: '0.01em',
              background: isSingle ? 'rgba(99, 102, 241, 0.08)' : 'rgba(13, 148, 136, 0.08)',
              color: isSingle ? '#4F46E5' : '#0D9488',
              border: `1px solid ${isSingle ? 'rgba(99, 102, 241, 0.22)' : 'rgba(13, 148, 136, 0.22)'}`,
              whiteSpace: 'nowrap'
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {isSingle ? <TargetIcon size={13} /> : <BooksIcon size={13} />}
            </span>
            <span>{val || 'Contest'}</span>
          </span>
        );

      },
    },
    {
      key: 'frequency',
      label: 'Frequency',
      render: (val) => <span style={{ fontSize: '13px', color: 'var(--clr-text-1)', fontWeight: 500 }}>{val || 'Self-Paced'}</span>
    },
    {
      key: 'quizzes_count',
      label: 'Quizzes',
      render: (_, row) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '26px',
            height: '24px',
            padding: '0 8px',
            borderRadius: '12px',
            background: 'var(--clr-background, #F4F4F5)',
            border: '1px solid var(--clr-border, #E4E4E7)',
            fontSize: '12px',
            fontWeight: 700,
            color: 'var(--clr-text-1)'
          }}
        >
          {Number(row.quizzes_count) || 0}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => {
        const st = val || 'under-review';
        const cfg = SEASON_STATUS_CONFIG[st] || SEASON_STATUS_CONFIG['under-review'];
        const theme = statusThemeMap[st] || statusThemeMap['under-review'];
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '11.5px',
              fontWeight: 600,
              background: theme.bg,
              color: theme.color,
              border: `1px solid ${theme.border}`,
              whiteSpace: 'nowrap'
            }}
          >
            <span style={{ fontSize: '12px', lineHeight: 1 }}>{cfg.icon}</span>
            <span>{cfg.label}</span>
          </span>
        );
      },
    },
    {
      key: 'role',
      label: 'My Role',
      render: (_, row) => {
        const r = getSeasonRole(row);
        const conf = roleMap[r] || roleMap['Owner'];

        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '11.5px',
              fontWeight: 600,
              background: conf.bg,
              color: conf.text,
              border: `1px solid ${conf.border}`,
              whiteSpace: 'nowrap'
            }}
          >
            <span style={{ fontSize: '11px', lineHeight: 1 }}>{conf.icon}</span>
            <span>{r}</span>
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row, rowIndex) => {
        const isOpen = openMenuId === row.id;
        const canDeactivateSeason = isSeasonOwner(row);
        const isUnderReview = row.status === 'under-review';
        const isRejected = row.status === 'rejected';
        const isSuspended = row.status === 'suspended';
        const canAccessDashboard = !isUnderReview && !isRejected && !isSuspended;
        const canShare = !isUnderReview && !isRejected && !isSuspended;
        const isNearBottom = rowIndex >= (pageSeasonsData.length - 2) && pageSeasonsData.length > 2;

        return (
          <div className="table-actions-menu" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="table-actions-trigger"
              onClick={() => setOpenMenuId(isOpen ? null : row.id)}
              aria-label="Actions"
            >
              ⋮
            </button>
            {isOpen && (
              <div className={`table-actions-dropdown ${isNearBottom ? 'table-actions-dropdown--up' : ''}`}>
                {canAccessDashboard && (
                  <button
                    type="button"
                    className="table-actions-item"
                    onClick={() => {
                      setOpenMenuId(null);
                      handleOpenSeasonDashboard(row);
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}><BarChartIcon size={14} /></span>
                    <span>Dashboard ↗</span>
                  </button>
                )}
                {canShare && (
                  <button
                    type="button"
                    className="table-actions-item"
                    onClick={() => {
                      setOpenMenuId(null);
                      handleShareSeason(row);
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}><LinkIcon size={14} /></span>
                    <span>Share</span>
                  </button>
                )}
                {canDeactivateSeason && (
                  <button
                    type="button"
                    className="table-actions-item table-actions-item--danger"
                    onClick={() => {
                      setOpenMenuId(null);
                      handleDeactivateSeason(row);
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}><TrashIcon size={14} /></span>
                    <span>Deactivate</span>
                  </button>
                )}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  // Redirect to About Us if unauthenticated
  useEffect(() => {
    if (!user) {
      navigate(ROUTES.PUBLIC.HOME, { replace: true });
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  return (
    <div className="studio-container">
      {/* Executive Hero Banner */}
      <div className="studio-header">
        <div className="studio-header-content">
          <div className="studio-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <LightningIcon size={14} />
            <span>Host &amp; League Studio</span>
          </div>
          <h1 className="studio-title">
            Inspire &amp; Engage Your Scripture Community
          </h1>
          <p className="studio-subtitle">
            Create interactive Bible leagues, publish chapter-based quizzes, host live contest rounds, and track your participants' real-time rankings and spiritual growth.
          </p>
        </div>
        <button
          type="button"
          className="studio-create-btn"
          onClick={() => navigate(ROUTES.STUDIO.CREATE_SEASON)}
        >
          <span>+</span>
          <span>Launch a New League ↗</span>
        </button>
      </div>

      {/* KPI Analytics Grid */}
      <div className="studio-kpi-grid">
        <div className="studio-kpi-card">
          <div className="studio-kpi-icon-wrap" style={{ background: '#FFF4F3', color: '#FF5C56', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrophyIcon size={22} />
          </div>
          <div className="studio-kpi-info">
            <span className="studio-kpi-value">{nonDeactivatedSeasons.length}</span>
            <span className="studio-kpi-label">Total Leagues</span>
          </div>
        </div>

        <div className="studio-kpi-card">
          <div className="studio-kpi-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LightningIcon size={22} />
          </div>
          <div className="studio-kpi-info">
            <span className="studio-kpi-value">{activeLeagues.length}</span>
            <span className="studio-kpi-label">Live &amp; Active</span>
          </div>
        </div>

        <div className="studio-kpi-card">
          <div className="studio-kpi-icon-wrap" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BibleIcon size={22} />
          </div>
          <div className="studio-kpi-info">
            <span className="studio-kpi-value">{totalQuizzes}</span>
            <span className="studio-kpi-label">Scripture Quizzes</span>
          </div>
        </div>

        <div className="studio-kpi-card">
          <div className="studio-kpi-icon-wrap" style={{ background: 'rgba(249, 115, 22, 0.1)', color: '#EA580C', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CrownIcon size={22} />
          </div>
          <div className="studio-kpi-info">
            <span className="studio-kpi-value" style={{ fontSize: '18px', lineHeight: '24px' }}>
              Community Host
            </span>
            <span className="studio-kpi-label">Host Status</span>
          </div>
        </div>
      </div>


      {/* Studio Controls Bar: Status Filter Tabs + Search + View Switcher */}
      <div className="studio-controls-bar">
        <div className="studio-filter-tabs">
          <button
            type="button"
            className={`studio-tab-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('all'); setSeasonsPage(1); }}
          >
            <span>All Leagues</span>
            <span className="studio-tab-count">{nonDeactivatedSeasons.length}</span>
          </button>
          <button
            type="button"
            className={`studio-tab-btn ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('active'); setSeasonsPage(1); }}
          >
            <span>Active</span>
            <span className="studio-tab-count">{activeLeagues.length}</span>
          </button>
          <button
            type="button"
            className={`studio-tab-btn ${statusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('approved'); setSeasonsPage(1); }}
          >
            <span>In Setup</span>
            <span className="studio-tab-count">{approvedLeagues.length}</span>
          </button>
          <button
            type="button"
            className={`studio-tab-btn ${statusFilter === 'under-review' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('under-review'); setSeasonsPage(1); }}
          >
            <span>Under Review</span>
            <span className="studio-tab-count">{reviewLeagues.length}</span>
          </button>
          {completedLeagues.length > 0 && (
            <button
              type="button"
              className={`studio-tab-btn ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => { setStatusFilter('completed'); setSeasonsPage(1); }}
            >
              <span>Completed</span>
              <span className="studio-tab-count">{completedLeagues.length}</span>
            </button>
          )}
        </div>

        <div className="studio-controls-right">
          <div className="studio-search-input-wrap">
            <span className="studio-search-icon" style={{ display: 'flex', alignItems: 'center' }}>
              <SearchIcon size={15} />
            </span>
            <input
              type="text"
              className="studio-search-input"
              value={seasonsSearch}
              onChange={(e) => { setSeasonsSearch(e.target.value); setSeasonsPage(1); }}
              placeholder="Search leagues by title or description..."
            />
          </div>


          <div className="studio-view-toggle">
            <button
              type="button"
              className={`studio-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Cards Grid View"
              aria-label="Cards Grid View"
            >
              ⊞
            </button>
            <button
              type="button"
              className={`studio-view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Data Table View"
              aria-label="Data Table View"
            >
              ☰
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {loadingSeasons ? (
        <div style={{ padding: '64px 20px', textAlign: 'center', color: 'var(--clr-muted)' }}>
          <Spinner />
          <p style={{ marginTop: '16px', fontSize: '14.5px', fontWeight: 600 }}>Loading your host studio...</p>
        </div>
      ) : nonDeactivatedSeasons.length === 0 ? (
        /* Empty State: 4-step Onboarding Roadmap */
        <div className="studio-empty-card">
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📖</div>
          <h2 style={{ fontSize: '22px', fontWeight: 900, margin: '0 0 8px 0', color: 'var(--clr-text-1)' }}>
            Welcome to Host & League Studio
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--clr-muted)', maxWidth: '520px', margin: '0 auto 24px' }}>
            You haven't created any Scripture leagues yet. Follow these simple steps to kickstart your first tournament!
          </p>

          <div className="studio-roadmap-grid studio-roadmap-grid--4">
            <div className="studio-roadmap-step">
              <div className="studio-step-num">1</div>
              <h4>Create League</h4>
              <p>Configure your league title, choose daily or self-paced frequency, and set your target Scripture passages.</p>
            </div>
            <div className="studio-roadmap-step">
              <div className="studio-step-num">2</div>
              <h4>Review &amp; Approval</h4>
              <p>Bible Contest Team will review and approve after tool training.</p>
            </div>
            <div className="studio-roadmap-step">
              <div className="studio-step-num">3</div>
              <h4>Add Quizzes &amp; Bank</h4>
              <p>Craft custom questions with Bible verse citations, multiple-choice options, hints, and time limits.</p>
            </div>
            <div className="studio-roadmap-step">
              <div className="studio-step-num">4</div>
              <h4>Invite &amp; Track Live</h4>
              <p>Share instant player links with your community and monitor live leaderboard standings as participants submit answers.</p>
            </div>
          </div>

          <button
            type="button"
            className="studio-create-btn"
            onClick={() => navigate(ROUTES.STUDIO.CREATE_SEASON)}
            style={{ margin: '0 auto' }}
          >
            <span>+</span>
            <span>Create Your First League ↗</span>
          </button>
        </div>
      ) : filteredSeasons.length === 0 ? (
        /* No Matches Filter State */
        <div className="studio-empty-card" style={{ padding: '40px 20px' }}>
          <div style={{ fontSize: '38px', marginBottom: '12px' }}>🔍</div>
          <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 8px 0', color: 'var(--clr-text-1)' }}>
            No leagues match your filter
          </h3>
          <p style={{ fontSize: '13.5px', color: 'var(--clr-muted)', margin: '0 auto 18px', maxWidth: '360px' }}>
            We couldn't find any leagues matching "{seasonsSearch || statusFilter}". Try adjusting your keywords or clearing the filter.
          </p>
          <button
            type="button"
            className="studio-action-share-btn"
            onClick={() => { setSeasonsSearch(''); setStatusFilter('all'); }}
            style={{ margin: '0 auto' }}
          >
            Clear Filters
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        /* Executive Grid Cards View */
        <div>
          <div className="studio-grid">
            {pageSeasonsData.map((row) => {
              const isUnderReview = row.status === 'under-review';
              const isRejected = row.status === 'rejected';
              const isSuspended = row.status === 'suspended';
              const canAccessDashboard = !isUnderReview && !isRejected && !isSuspended;
              const canShare = !isUnderReview && !isRejected && !isSuspended;
              const canDeactivateSeason = isSeasonOwner(row);

              const st = row.status || 'under-review';
              const cfg = SEASON_STATUS_CONFIG[st] || SEASON_STATUS_CONFIG['under-review'];
              const statusTheme = statusThemeMap[st] || statusThemeMap['under-review'];

              const role = getSeasonRole(row);
              const roleConf = roleMap[role] || roleMap['Owner'];
              const isSingle = row.type === 'Single Contest';
              const isOpen = openMenuId === row.id;

              return (
                <div key={row.id} className="studio-league-card">
                  <div>
                    {/* Card Head */}
                    <div className="studio-card-head">
                      <div className="studio-card-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {row.logo ? (
                          <img src={row.logo} alt={row.season} />
                        ) : (
                          isSingle ? <TargetIcon size={20} style={{ color: '#4F46E5' }} /> : <TrophyIcon size={20} style={{ color: '#D97706' }} />
                        )}
                      </div>
                      <div className="studio-card-head-info">
                        <h3
                          className="studio-card-title"
                          onClick={() => handleOpenSeasonDashboard(row)}
                          title={isUnderReview ? 'The season is under review, you will get notified once the season is ready to host.' : row.season}
                          style={{
                            cursor: canAccessDashboard ? 'pointer' : 'default'
                          }}
                        >
                          {row.season}
                        </h3>
                        <div className="studio-card-badges">
                          <span className="studio-type-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            {isSingle ? <><TargetIcon size={12} /> Single Contest</> : <><BooksIcon size={12} /> Tournament Series</>}
                          </span>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              fontSize: '11px',
                              fontWeight: 700,
                              background: statusTheme.bg,
                              color: statusTheme.color,
                              border: `1px solid ${statusTheme.border}`
                            }}
                          >
                            <span>{cfg.icon}</span>
                            <span>{cfg.label}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Description */}
                    <p className="studio-card-desc">
                      {row.description || 'No description provided for this Scripture tournament.'}
                    </p>

                    {/* Card Stats Bar */}
                    <div className="studio-card-stats">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}><BooksIcon size={13} style={{ color: 'var(--clr-muted)' }} /></span>
                        <span><strong>{Number(row.quizzes_count) || 0}</strong> Quizzes</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}><ClockIcon size={13} style={{ color: 'var(--clr-muted)' }} /></span>
                        <span>{row.frequency || 'Self-Paced'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>{roleConf.icon}</span>
                        <span style={{ fontWeight: 600, color: roleConf.text }}>{role}</span>
                      </div>
                    </div>

                    {isUnderReview && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          background: 'rgba(245, 158, 11, 0.08)',
                          border: '1px solid rgba(245, 158, 11, 0.25)',
                          color: '#B45309',
                          fontSize: '12px',
                          lineHeight: 1.45,
                          marginTop: '12px',
                          fontWeight: 500
                        }}
                      >
                        <span style={{ fontSize: '15px', lineHeight: 1, flexShrink: 0 }}>⏳</span>
                        <span>The season is under review, you will get notified once the season is ready to host.</span>
                      </div>
                    )}
                  </div>

                  {/* Card Actions Footer */}
                  <div className="studio-card-actions">
                    {isUnderReview ? (
                      <button
                        type="button"
                        className="studio-action-manage-btn studio-action-manage-btn--review"
                        onClick={() => handleOpenSeasonDashboard(row)}
                        title="The season is under review, you will get notified once the season is ready to host."
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>⏳</span>
                        <span>Under Review</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="studio-action-manage-btn"
                        onClick={() => handleOpenSeasonDashboard(row)}
                        style={{
                          opacity: canAccessDashboard ? 1 : 0.7,
                          cursor: canAccessDashboard ? 'pointer' : 'default'
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}><BarChartIcon size={14} /></span>
                        <span>{canAccessDashboard ? 'Manage Dashboard ↗' : 'View Status'}</span>
                      </button>
                    )}

                    {canShare && (
                      <button
                        type="button"
                        className="studio-action-share-btn"
                        onClick={() => handleShareSeason(row)}
                        title="Share League Link"
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}><LinkIcon size={14} /></span>
                        <span>Share</span>
                      </button>
                    )}

                    {canDeactivateSeason && (
                      <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="studio-action-more-btn"
                          onClick={() => setOpenMenuId(isOpen ? null : row.id)}
                          aria-label="More options"
                          title="More options"
                        >
                          ⋮
                        </button>
                        {isOpen && (
                          <div
                            className="table-actions-dropdown table-actions-dropdown--up"
                            style={{ right: 0, minWidth: '150px' }}
                          >
                            <button
                              type="button"
                              className="table-actions-item table-actions-item--danger"
                              onClick={() => {
                                setOpenMenuId(null);
                                handleDeactivateSeason(row);
                              }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center' }}><TrashIcon size={14} /></span>
                              <span>Deactivate</span>
                            </button>
                          </div>
                        )}
                      </div>

                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalSeasonsPages > 1 && (
            <div style={{ marginTop: '24px' }}>
              <Pagination
                currentPage={seasonsPage}
                totalPages={totalSeasonsPages}
                onChange={setSeasonsPage}
              />
            </div>
          )}
        </div>
      ) : (
        /* Data Table View */
        <div style={{ background: '#FFFFFF', borderRadius: '18px', border: '1px solid var(--clr-border, #E4E4E7)', padding: '16px', overflowX: 'auto' }}>
          <Table
            columns={seasonsColumns}
            data={pageSeasonsData}
            loading={loadingSeasons}
            emptyText="No leagues found."
            rowKey="id"
          />

          {totalSeasonsPages > 1 && (
            <div style={{ marginTop: '20px' }}>
              <Pagination
                currentPage={seasonsPage}
                totalPages={totalSeasonsPages}
                onChange={setSeasonsPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManageMyQuizzes;
